import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Panel } from "@/components/dash";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { inputCls, btnPrimary, btnGhost } from "@/components/admin/AdminLayout";
import { photoUrl } from "@/components/site/Testimonials";

const PAGES = [["home", "Home"], ["tech-transfer", "Tech Transfer"], ["plant-advisory", "Plant Advisory"]];
const EMPTY = { name: "", job_title: "", company: "", quote: "", photo_id: "", pages: PAGES.map((p) => p[0]), order: 50, published: true };

function PhotoField({ value, onChange }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try { onChange((await api.post("/admin/testimonials/photo", fd)).data.id); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <div className="flex items-center gap-3">
      {value ? <img src={photoUrl(value)} alt="" className="h-14 w-14 rounded-full object-cover" data-testid="testimonial-photo-preview" /> : <span className="h-14 w-14 rounded-full bg-slate-800" />}
      <label className={`${btnGhost} flex items-center cursor-pointer`}>{busy ? "Uploading…" : value ? "Change picture" : "Upload picture"}
        <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={upload} data-testid="testimonial-photo-input" /></label>
      {value && <button type="button" className={btnGhost} onClick={() => onChange("")}>Remove</button>}
    </div>
  );
}

function Editor({ item, onClose }) {
  const [f, setF] = useState({ ...EMPTY, ...item });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : k === "order" ? Number(e.target.value) : e.target.value });
  const togglePage = (p) => setF({ ...f, pages: f.pages.includes(p) ? f.pages.filter((x) => x !== p) : [...f.pages, p] });
  const save = async () => {
    const body = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, f[k]]));
    try { if (item.id) await api.put(`/admin/testimonials/${item.id}`, body); else await api.post("/admin/testimonials", body); toast.success("Saved"); onClose(true); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={() => onClose(false)}>
      <DialogContent className="bg-slate-950 border-slate-800 max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{item.id ? "Edit testimonial" : "New testimonial"}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <PhotoField value={f.photo_id} onChange={(v) => setF({ ...f, photo_id: v })} />
          <input className={inputCls} placeholder="Customer name" value={f.name} onChange={set("name")} data-testid="testimonial-name" />
          <div className="grid grid-cols-2 gap-3">
            <input className={inputCls} placeholder="Job title" value={f.job_title} onChange={set("job_title")} data-testid="testimonial-job-title" />
            <input className={inputCls} placeholder="Company name" value={f.company} onChange={set("company")} data-testid="testimonial-company" />
          </div>
          <textarea className={`${inputCls} h-28 py-2`} placeholder="Quote" value={f.quote} onChange={set("quote")} data-testid="testimonial-quote" />
          <div className="flex flex-wrap gap-4 text-sm">{PAGES.map(([k, l]) => (
            <label key={k} className="flex items-center gap-2"><input type="checkbox" checked={f.pages.includes(k)} onChange={() => togglePage(k)} data-testid={`testimonial-page-${k}`} /> {l}</label>))}
          </div>
          <div className="flex items-center gap-4">
            <input type="number" className={`${inputCls} w-24`} value={f.order} onChange={set("order")} title="Display order" />
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.published} onChange={set("published")} /> Published</label>
          </div>
          <button className={btnPrimary} onClick={save} disabled={!f.name || !f.quote} data-testid="testimonial-save">Save</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminTestimonials() {
  const [edit, setEdit] = useState(null);
  const { data = [], refetch } = useQuery({ queryKey: ["admin-testimonials"], queryFn: async () => (await api.get("/admin/testimonials")).data });
  const del = async (id) => { if (window.confirm("Delete this testimonial?")) { await api.delete(`/admin/testimonials/${id}`); refetch(); } };
  return (
    <div className="space-y-6">
      <PageHeader eyebrow="CMS · Testimonials" title="Customer Testimonials" desc="Shown on Home, Tech Transfer and Plant Advisory pages. The section stays hidden until at least one testimonial is published.">
        <button className={btnPrimary} onClick={() => setEdit({})} data-testid="testimonial-new">+ New testimonial</button>
      </PageHeader>
      <Panel>
        <table className="w-full text-sm" data-testid="admin-testimonials-table">
          <thead className="bg-slate-900/80"><tr>{["Customer", "Company", "Pages", "Status", ""].map((h) => <th key={h} className="px-3 py-2 text-left font-mono text-[11px] uppercase text-slate-400">{h}</th>)}</tr></thead>
          <tbody>{data.map((t) => (
            <tr key={t.id} className="border-t border-slate-800/70">
              <td className="px-3 py-2">{t.name}<span className="block text-xs text-slate-500">{t.job_title}</span></td>
              <td className="px-3 py-2 text-slate-400">{t.company}</td>
              <td className="px-3 py-2 text-xs text-slate-400">{t.pages.join(", ")}</td>
              <td className="px-3 py-2 text-xs">{t.published ? "Live" : "Hidden"}</td>
              <td className="px-3 py-2 flex gap-1"><button className={btnGhost} onClick={() => setEdit(t)} data-testid={`testimonial-edit-${t.id}`}>Edit</button>
                <button className={btnGhost} onClick={() => del(t.id)} data-testid={`testimonial-delete-${t.id}`}>Delete</button></td>
            </tr>))}
            {!data.length && <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-500" data-testid="testimonials-empty">No testimonials yet.</td></tr>}
          </tbody>
        </table>
      </Panel>
      {edit && <Editor item={edit} onClose={(ok) => { setEdit(null); if (ok) refetch(); }} />}
    </div>
  );
}
